import styled from '@emotion/styled';
import { captureEvent, captureException } from '@sentry/browser';
import moment from 'moment';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { BrandWrapper } from 'src/modules/onboarding/components/BrandWrapper';
import { getVideoSessions } from 'src/modules/videoSessions/selectors/videoSessionsSelectors';
import { Button, ButtonGroup } from '@modernhealth/vital-components';
import { getTimeDiffFromNow } from 'src/shared/helpers/getTimeDiffFromNow';
import { hexToRgba } from 'src/shared/helpers/hexToRgba';
import { color, font } from 'src/shared/styles/variables';

import gChartIcon from 'src/public/img/g-chart.svg';
import handHeartIcon from 'src/public/img/hand-heart.svg';
import privacyPolicyIcon from 'src/public/img/privacy-policy.svg';

const Wrapper = styled.div({
    maxWidth: 492,
    width: '100%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    paddingBottom: 100,
});

const HeaderText = styled.h3({
    color: color.pickledBluewood,
    marginBottom: 15,
    ...font.black(font.size.extraLarge),
});

const DescriptionText = styled.p({
    color: hexToRgba(color.blueBayoux, 0.9),
    marginBottom: 25,
    lineHeight: '24px',
    ...font.book(font.size.medium),
});

const ListHederText = styled.p({
    color: hexToRgba(color.ebonyClay, 0.9),
    marginBottom: 10,
    ...font.heavy(font.size.medium),
});

const List = styled.ul({
    paddingLeft: 0,
});

const ListItemWrapper = styled.li({
    display: 'flex',
    alignItems: 'center',
    marginBottom: 13,
});

const ListItemIconWrapper = styled.div({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: hexToRgba('#607BEF', 0.1),
});

const ListItemText = styled.p({
    color: hexToRgba(color.blueBayoux, 0.9),
    marginLeft: 8,
    ...font.book(font.size.medium),
});

type Props = RouteComponentProps<
    {},
    {},
    {
        sessionUuid?: string;
    }
>;

export const PRE_SESSION_ASSESSMENTS_DISPLAYED_DATE_STORAGE_KEY =
    'pre_session_assessments_displayed_date';

export enum PreSessionAssessmentsIntroPageTestID {
    Wrapper = 'PreSessionAssessmentsIntroPageTestID.Wrapper',
}

export const PreSessionAssessmentsIntroPage: React.FC<Props> = ({ location, history }) => {
    const { t } = useTranslation();
    const sessions = useSelector(getVideoSessions);
    const sessionUuid = location?.state?.sessionUuid;

    useEffect(() => {
        try {
            if (!sessionUuid) {
                throw new Error(
                    '[Pre session assessments deep link] Session UUID was not provided',
                );
            }

            localStorage.setItem(
                PRE_SESSION_ASSESSMENTS_DISPLAYED_DATE_STORAGE_KEY,
                moment().toISOString(),
            );

            const session = sessions.find(({ uuid }) => sessionUuid === uuid);

            if (!session) {
                captureEvent({
                    message:
                        '[Pre session assessments deep link] Could not find a session using provided UUID',
                });
            } else {
                const scheduledStartTime = moment(session?.scheduledTime);
                const minutesDiff = getTimeDiffFromNow(scheduledStartTime, 'minutes');

                // check if user is 2 minutes or more late to the sessions
                // we don't want to take users to the assessments if they're already late
                if (minutesDiff <= -2) {
                    let url = '/';

                    // check if still can join the session
                    if (minutesDiff > (session?.scheduledDuration || 60) * -1) {
                        url = `/video-session/${sessionUuid}`;
                    }

                    history.replace(url);
                }
            }
        } catch (err) {
            captureException(err);
            history.replace('/');
        }
    }, [sessionUuid, sessions]);

    return sessionUuid ? (
        <BrandWrapper>
            <Wrapper data-testid={PreSessionAssessmentsIntroPageTestID.Wrapper}>
                <HeaderText>{t('assessments:pre_session_assessment_intro.title')}</HeaderText>
                <DescriptionText>
                    {t('assessments:pre_session_assessment_intro.description')}
                </DescriptionText>

                <ListHederText>
                    {t('assessments:pre_session_assessment_intro.list_title')}
                </ListHederText>
                <List>
                    <ListItemWrapper>
                        <ListItemIconWrapper>
                            <img src={handHeartIcon} alt="icon" />
                        </ListItemIconWrapper>
                        <ListItemText>
                            {t('assessments:pre_session_assessment_intro.list_item_1')}
                        </ListItemText>
                    </ListItemWrapper>
                    <ListItemWrapper>
                        <ListItemIconWrapper>
                            <img src={gChartIcon} alt="icon" />
                        </ListItemIconWrapper>
                        <ListItemText>
                            {t('assessments:pre_session_assessment_intro.list_item_2')}
                        </ListItemText>
                    </ListItemWrapper>
                    <ListItemWrapper>
                        <ListItemIconWrapper>
                            <img src={privacyPolicyIcon} alt="icon" />
                        </ListItemIconWrapper>
                        <ListItemText>
                            {t('assessments:pre_session_assessment_intro.list_item_3')}
                        </ListItemText>
                    </ListItemWrapper>
                </List>

                <ButtonGroup>
                    <Button onClick={() => history.replace('/')} variant="secondary">
                        {t('common.not_now')}
                    </Button>
                    <Button
                        variant="primary"
                        onClick={() => history.push('/pre-session-assessments', { sessionUuid })}
                    >
                        {t('assessments:pre_session_assessment_intro.check_wellbeing')}
                    </Button>
                </ButtonGroup>
            </Wrapper>
        </BrandWrapper>
    ) : null;
};
